import React, {
	useMemo,
	useRef,
	useState,
	useEffect,
	MouseEvent,
	useDeferredValue,
	useCallback,
} from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import { track } from '@amplitude/analytics-browser';
import Icon, { ICONS, COLORS } from '../general-components/icon';
import type { MediaItem, PresentedMediaItem } from '../../stores/types/media';
import Thumbnail from '../chat/chat-document-thumbnail';
import Dialog from '../general-components/dialog';
import { ROOM_STATES } from '../../stores/reducers/settings';
import useMedia from '../../hooks/use-media';
import { showAlert } from '../general-components/alert-service';
import {
	AUDIO_TYPES,
	DOC_TYPES,
	DOWNLOAD_TYPES,
	MEDIA_TYPES,
} from '../../types/constants';
import type { Shot } from '../../stores/types/shots';
import { MODAL_TEMPLATES } from '../../stores/reducers/modal';
import { VIDEO_EDITOR_PAGES } from '../modal/templates/video-editor/video-editor';
import { setModalState } from '../../stores/actions/modal';
import { DRAG_TYPE } from '../../stores/reducers/user-interface';
import useDragItem from '../../hooks/use-dnd/use-drag-item';
import { downloadMedia, getCurrentPageThumb } from '../../utils/media';
import { AmpEvent } from '../../types/amplitude';
import ExpandedMediaLink from './expanded-media-link';
import LoadingSpinner from '../general-components/loading-spinner';
import { updateSingleItem } from '../../socket-listeners/media';
import AudioThumbnail from './audio-thumbnail';
import colors from '../../utils/colors';

export interface MediaProps {
	mediaItem: MediaItem;
	onPreview: Function;
	onDelete: Function;
	onDownload: Function;
	onConvertToOverlay: Function;
	cancelMediaItemUpload: Function;
	user: any;
	progress?: number;
	currentlyPresented?: PresentedMediaItem;
	isHost: boolean;
	thisMediaIsDownloading: boolean;
	roomState: number;
	selectForSlideView: Function;
	isForShotModal: boolean;
	currentShot?: Shot;
	toggleShareView: Function;
	index: number;
	isOnCanvas: boolean;
}

const expandedMedia: any = {};

export default function MediaLink(props: MediaProps) {
	const {
		mediaItem,
		onPreview,
		onDelete,
		onDownload,
		onConvertToOverlay,
		cancelMediaItemUpload,
		user,
		progress,
		currentlyPresented,
		isHost,
		thisMediaIsDownloading,
		roomState,
		selectForSlideView,
		isForShotModal,
		toggleShareView,
		index,
		isOnCanvas,
	} = props;

	const {
		canEditOrControlMedia,
		canDeleteMedia,
		canTrimMediaItem,
		canDownloadMedia,
	} = useMedia();

	const dispatch = useDispatch();
	const dialog = useRef<any>(null);
	const cancelButtonRef = useRef(undefined as any);
	const [cancelButtonX, setCancelButtonX] = useState<number | undefined>();
	const [cancelButtonY, setCancelButtonY] = useState<number | undefined>();
	const [mouseY, setMouseY] = useState<string>('');
	const [showMenu, setShowMenu] = useState(false);
	const [mediaIsExpanded, setMediaIsExpanded] = useState<boolean>(
		expandedMedia?.[mediaItem.id] ?? false,
	);
	const ellipsesOpen = useRef(false);
	// used to prevent both click listeners from firing at once
	const deferredShowMenu = useDeferredValue(showMenu);

	const onScroll = () => {
		if (ellipsesOpen.current) {
			ellipsesOpen.current = false;
			setShowMenu(false);
		}
	};

	useEffect(() => {
		const scrollableDiv = document.getElementById('media-tab');
		if (scrollableDiv) {
			scrollableDiv.addEventListener('scroll', onScroll, false);
		}

		return () => {
			if (scrollableDiv) {
				scrollableDiv.removeEventListener('scroll', onScroll);
			}
		};
	}, []);

	const displayName = mediaItem.custom_name
		|| mediaItem.original_name?.replace(/\.[^.]+$/, '')
		|| '';

	const mediaThumbnailURL = useMemo(() => {
		return mediaItem.thumbnail_url ?? getCurrentPageThumb(mediaItem.media_data);
	}, [mediaItem.media_data, mediaItem?.thumbnail_url]);

	const showCancelUploadDialog = (e: any) => {
		if (cancelButtonRef.current) {
			setCancelButtonX(cancelButtonRef.current
				? cancelButtonRef.current.getBoundingClientRect().left - 433
				: undefined);
			setCancelButtonY(cancelButtonRef.current
				? cancelButtonRef.current.getBoundingClientRect().top - 65
				: undefined);
			dialog.current.confirm(
				'Are you sure you want to cancel this upload?',
				'',
				'Yes',
				'No',
				(confirmed: boolean) => {
					if (confirmed) {
						cancelMediaItemUpload();
					}
				},
			);
			e.stopPropagation();
		}
	};
	const [userCanControl, userCanEdit, userCanDelete, userCanDownload] = useMemo(
		() => [
			canEditOrControlMedia(mediaItem, user, isHost, true),
			canEditOrControlMedia(mediaItem, user, isHost, false),
			canDeleteMedia(mediaItem, user, isHost),
			canDownloadMedia(mediaItem),
		],
		[isHost, mediaItem, user, props.currentShot],
	);

	const toggleEllipses = (e: MouseEvent<HTMLDivElement>) => {
		if (!ellipsesOpen.current) {
			let { pageY } = e;

			/*
				Note => Green-3529:
				The media link context menu needs a refactor as mentioned in
				the ticket. This is a temporary fix to avoid menu items going
				completely off screen and being unreadable in the mean time.
			*/
			const extraHeight = userCanDownload ? 37 : 0;

			if (pageY + 175 > window.innerHeight && userCanDelete) {
				pageY = window.innerHeight - 175 - extraHeight;
			} else if (pageY + 100 > window.innerHeight && userCanEdit && !userCanDelete) {
				pageY = window.innerHeight - 110 - extraHeight;
			} else if (pageY + 50 > window.innerHeight) {
				pageY = window.innerHeight - 60 - extraHeight;
			} else {
				pageY = 40;
			}

			setMouseY(`${pageY}px`);
		}
		ellipsesOpen.current = !ellipsesOpen.current;
		setShowMenu(ellipsesOpen.current);
	};

	function handleClick() {
		ellipsesOpen.current = false;
		setShowMenu(false);
	}

	useEffect(() => {
		if (deferredShowMenu) {
			window.addEventListener('click', handleClick);
		}

		return () => {
			window.removeEventListener('click', handleClick);
		};
	}, [deferredShowMenu]);

	const handlePreview = () => {
		ellipsesOpen.current = false;
		setShowMenu(false);
		onPreview();
	};

	const handleDelete = () => {
		ellipsesOpen.current = false;
		setShowMenu(false);
		onDelete();
	};

	const handleDownload = (mediaItem: MediaItem, isModal: boolean = false, e?: MouseEvent<HTMLDivElement>) => {
		e?.stopPropagation();
		ellipsesOpen.current = false;
		setShowMenu(false);

		if (isModal) {
			dispatch(setModalState({
				modalOpen: true,
				modalTemplate: MODAL_TEMPLATES.RECORDING_FAILED,
				modalData: {linkToRecording: mediaItem.images}
			}));
		} else {
			onDownload(mediaItem.type === MEDIA_TYPES.HLS
				? DOWNLOAD_TYPES.RECORDING
				: DOWNLOAD_TYPES.FILE);
		}

	};

	const canConvertToOverlay = useMemo((): boolean => (
		!(mediaItem.num_pages > 1 || DOC_TYPES?.includes(mediaItem.type))
	), [mediaItem]);

	const handleToOverlay = () => {
		if (canConvertToOverlay) {
			ellipsesOpen.current = false;
			setShowMenu(false);
			onConvertToOverlay();
		} else {
			showAlert({
				message: 'Cannot Create Overlay',
				description: 'Multi-page media items or documents cannot be used',
				duration: 4000,
			});
		}
	};

	const handleTrim = () => {
		track(AmpEvent.clickedTrim);
		dispatch(setModalState({
			modalOpen: true,
			modalTemplate: MODAL_TEMPLATES.VIDEO_EDITOR,
			modalData: {
				mediaId: mediaItem.id,
				editorPage: mediaItem.num_pages > 1
					? VIDEO_EDITOR_PAGES.SELECT_MEDIA_PAGE
					: VIDEO_EDITOR_PAGES.TRIM_EDITOR,
			},
		}));
	};

	const ellipsesShouldBeVisible = useMemo(() => {
		if (isForShotModal || thisMediaIsDownloading || !mediaThumbnailURL) { return false; }

		if (
			userCanDelete
			|| userCanEdit
			|| (roomState !== ROOM_STATES.EDITING && userCanControl)
		) {
			return true;
		}

		return false;
	}, [mediaItem, roomState, userCanControl, userCanDelete, userCanEdit,
		thisMediaIsDownloading, mediaThumbnailURL, isForShotModal]);

	const canTrim = useMemo((): boolean => (
		canTrimMediaItem(mediaItem)
	), [canTrimMediaItem, mediaItem]);

	const isCurrentlyDraggable = useMemo((): boolean => {
		// if the user leaves the room while a media video is uploading,
		// then their video will not upload, but
		// will still create the media link in the room.
		// The thumbnail for this item will be 'thumbs/' and
		// if it is put into the shot, our functions will error out.
		// Media refactor should look into maintaining
		// media uploads even if the page is refreshed
		if (isHost && mediaThumbnailURL) {
			return true;
		}
		return false;
	}, [mediaThumbnailURL, isHost]);

	const isAudioOnlyFile = useMemo((): boolean => AUDIO_TYPES.includes(mediaItem?.type ?? ''), [mediaItem?.type]);

	const [controlsHiddenForDrag, setControlsHiddenForDrag] = useState(false);
	const { dragRef, isDragging } = useDragItem(
		{
			id: mediaItem.id,
			type: AUDIO_TYPES.includes(mediaItem?.type ?? '')
				? DRAG_TYPE.FEED_MEDIA_AUDIO
				: DRAG_TYPE.FEED_MEDIA,
		},
		isCurrentlyDraggable,
	);

	const isPreviewable = useMemo(() => {
		return (userCanEdit && !!mediaThumbnailURL && !isAudioOnlyFile)
	}, [userCanEdit, mediaThumbnailURL, isAudioOnlyFile])

	/** double click handler - for quick access to the preview modal for a media item. */
	const quickMediaPreview = useCallback(() => {
		// validate permission for editing and media item must not be presenting currently .
		if (!isPreviewable) {
			return;
		}
		// close ellipse menu and open preview modal
		handlePreview();

		//these extra props are because handlePreview is not memoized, I believe?
	}, [mediaItem, currentlyPresented, isPreviewable]);

	const getEllipses = () => (
		<div className="media-dots-wrap" onClick={toggleEllipses}>
			<Icon name={ICONS.THREE_DOTS_HORIZONTAL} size={16} color={COLORS.WHITE} />
		</div>
	);

	const expandMediaLink = () => {
		if (
			mediaItem.type === MEDIA_TYPES.HLS
			&& !mediaItem.original_id
		) {
			if (mediaIsExpanded) {
				delete expandedMedia[mediaItem.id];
			} else {
				expandedMedia[mediaItem.id] = true;
				updateSingleItem({ message: { id: mediaItem.id } });
			}
			setMediaIsExpanded(!mediaIsExpanded);
		}
	};

	const canShowExpandCarrot = useMemo(() => (
		(ellipsesShouldBeVisible || isForShotModal)
		&& mediaItem.type === MEDIA_TYPES.HLS
		&& !mediaItem.original_id
	), [ellipsesShouldBeVisible, isForShotModal, mediaItem]);

	const SlidesBtn = useMemo(() => (
		(userCanEdit || userCanControl || userCanDelete)
		&& mediaItem.num_pages > 1
		&& !controlsHiddenForDrag
		&& !isForShotModal
			? <button className="go-to-slides" onClick={() => selectForSlideView(mediaItem.id)}>
				<span className='bl-font-s2'>Slides</span>
				<Icon size={10} color={COLORS.WHITE}
					name={ICONS.KEYBOARD_ARROW_RIGHT} />
			</button> : null
	), [
		mediaItem,
		userCanEdit,
		userCanControl,
		userCanDelete,
		selectForSlideView,
		isForShotModal,
		controlsHiddenForDrag,
	]);

	const thumbnailBackgroundColor = useMemo(() => {
		if (AUDIO_TYPES.includes(mediaItem.type)) {
			return 'transparent';
		}
		if (mediaItem.media_data.background_color) {
			return mediaItem.media_data.background_color;
		}
		return colors.darkerGray;
	}, [mediaItem.type, mediaItem.media_data.background_color]);

	const specialDownloadModal = useMemo(() => {
		if (
			mediaItem.type === MEDIA_TYPES.HLS
			&& mediaItem.convert_status === 'in_progress'
		) {
			const timeSinceStart = Date.now() - new Date(mediaItem.created_date).getTime();
			// show modal if the media has been processing for more than 4 hours
			return (timeSinceStart / 1000 / 60 / 60) > 4;
		}
		return false;
	}, [mediaItem.type, mediaItem.convert_status]);

	return (
		<>
			<>
				{isDragging && (<div className="media-link-drag-placeholder" />)}
				<div
					className={classNames('padding-top-small', {
						'link-is-highlighted': isOnCanvas,
						'link-is-dragging': isDragging,
					})}
				>
					<div
						ref={dragRef}
						id={`media-link-${index}`}
						onDragStart={() => {
							setControlsHiddenForDrag(true);
							setShowMenu(false);
							ellipsesOpen.current = false;
						}}
						onDragEnd={() => setControlsHiddenForDrag(false)}
						className={classNames(
							'media-link',
							{
								'cursor-grab': isCurrentlyDraggable,
								'cursor-pointer': isPreviewable && !isCurrentlyDraggable,
								'ellipses-open': showMenu,
								'is-expanded': mediaIsExpanded,
								'recording-media': mediaItem.type === DOWNLOAD_TYPES.RECORDING,
								'no-shrink': isForShotModal && !canShowExpandCarrot,
								short: !mediaIsExpanded && thisMediaIsDownloading,
							},
						)}
						onFocus={(e) => e.currentTarget.blur()}
						onDoubleClick={quickMediaPreview}
						onClick={expandMediaLink}
					>
						<div className="thumbnail">
							{!mediaThumbnailURL
								? (
									<div className="processing-container">
										<Icon name={ICONS.CHECK_PROCESSING} color={COLORS.PALE_GREEN} size={27}/>
										{SlidesBtn}
									</div>
								) : (
									<div
										className={classNames('thumbnail-container', mediaItem?.media_data.fill && 'fill')}
										style={{ background: thumbnailBackgroundColor }}
									>
										{AUDIO_TYPES.includes(mediaItem.type) && (
											<AudioThumbnail />
										)}
										{!AUDIO_TYPES.includes(mediaItem.type) && (<>
											<Thumbnail thumbnail={mediaThumbnailURL} />
											{SlidesBtn}
										</>)}
									</div>
								)}
						</div>

						<div className="media-details">
							<div className={`media-details-metadata ${(!mediaThumbnailURL && userCanDelete) ? 'uploading' : ''}`}>
								<p className="media-title bl-font-s3">{displayName}</p>
								<p className='media-description bl-font-c2'>
									{!mediaThumbnailURL ? 'Processing ...' : mediaItem.media_data.description}
								</p>
							</div>
							{!mediaThumbnailURL && userCanDelete
								&& <button ref={cancelButtonRef} className='delete-btn media-dots-wrap' onClick={showCancelUploadDialog}>
									<Icon name={ICONS.TRASH} color={COLORS.GRAY} size={16}/>
								</button>
							}

							{/* If "media-dots-wrap" className changes,
							modify getDeleteDialogPosition in media.tsx */}
							{ellipsesShouldBeVisible && (
								mediaItem.type !== MEDIA_TYPES.HLS
									|| (mediaItem.type === MEDIA_TYPES.HLS && mediaItem.original_id)
							) && getEllipses()}

							{!mediaIsExpanded && thisMediaIsDownloading && (
								<div className="media-download-spinner">
									<LoadingSpinner height={24} width={24} color={colors.textGray} stroke={10} />
								</div>
							)}

							{canShowExpandCarrot && (
								<div
									className='media-dots-wrap'
									onClick={expandMediaLink}>
									<Icon
										size={12} color={COLORS.WHITE}
										name={mediaIsExpanded ? ICONS.CARROT_UP : ICONS.CARROT_DOWN}
									/>
								</div>
							)}
						</div>

						<div
							className={classNames('media-ellipses-menu-container bl-font-p2', { open: showMenu })}
							style={{
								position: mouseY === '40px' ? 'absolute' : 'fixed',
								right: mouseY === '40px' ? '40px' : '160px',
								top: `${mouseY}`,
							}}
							data-testid='media-ellipses-menu-container'
						>
							<div className={'media-ellipses-menu'}>
								{canTrim && !isAudioOnlyFile && (
									<div className="media-ellipses-option media-ellipses-menu-row" onClick={handleTrim}>
										<div className="media-ellipses-menu-icon">
											<Icon name={ICONS.MEDIA_TRIM} size={12} color={COLORS.WHITE} />
										</div>
										<div className="media-ellipses-menu-text">Trim</div>
									</div>
								)}

								{userCanEdit && !isAudioOnlyFile && (
									<div className="media-ellipses-option media-ellipses-menu-row" onClick={handlePreview}>
										<div className="media-ellipses-menu-icon">
											<Icon name={ICONS.PREVIEW} size={12} color={COLORS.WHITE} />
										</div>
										<div className="media-ellipses-menu-text">Preview</div>
									</div>
								)}

								{isHost
								&& !isAudioOnlyFile
								&& mediaItem.type !== MEDIA_TYPES.HLS
								&& (
									<div
										className={`media-ellipses-option media-ellipses-menu-row ${canConvertToOverlay ? '' : 'media-ellipses-option-disabled'}`}
										onClick={handleToOverlay}
									>
										<div className="media-ellipses-menu-icon">
											<Icon name={ICONS.MEDIA_CONVERT} size={12} color={COLORS.WHITE} />
										</div>
										<div className="media-ellipses-menu-text">Convert to Overlay</div>
								</div>
								)}

								{userCanDownload && isHost && (
									<div
										className={classNames(
											'media-ellipses-option',
											'media-ellipses-menu-row',
											{
												'media-ellipses-option-disabled': mediaItem.type === MEDIA_TYPES.HLS && !mediaItem.recording_download_link,
												'media-ellipses-option-special-download': specialDownloadModal,
											}
										)}
										onClick={(e) => handleDownload(mediaItem, specialDownloadModal, e)}
									>
										<div className="media-ellipses-menu-icon">
											<Icon name={ICONS.MEDIA_DOWNLOAD} size={12} color={COLORS.WHITE} />
										</div>
										<div className="media-ellipses-menu-text">
											{mediaItem.type === MEDIA_TYPES.HLS && !mediaItem.recording_download_link ? '(Processing)' : 'Download'}
										</div>
									</div>
								)}

								{userCanEdit && !isAudioOnlyFile && (
									<div className="media-ellipses-option media-ellipses-menu-row" onClick={() => toggleShareView(mediaItem)}>
										<div className="media-ellipses-menu-icon">
											<Icon name={ICONS.MEDIA_SHARE} size={12} color={COLORS.WHITE} />
										</div>
										<div className="media-ellipses-menu-text">Share Controls</div>
									</div>
								)}

								{userCanDelete && (
									<div
										onClick={handleDelete}
										className="media-ellipses-option media-ellipses-menu-row"
										style={{ borderTop: `1px solid ${colors.blGrayscale700}` }}
									>
										<div className="media-ellipses-menu-icon">
											<Icon name={ICONS.MEDIA_DELETE} size={12} color={COLORS.RED} />
										</div>
										<div className="media-ellipses-menu-text" style={{ color: colors.blSemanticRed400 }}>Delete</div>
									</div>
								)}
							</div>
						</div>
					</div>
					{mediaIsExpanded && (
						<ExpandedMediaLink
							isCurrentlyUploading={!mediaThumbnailURL}
							mediaItem={mediaItem}
							mediaThumbnailURL={mediaThumbnailURL}
							progress={progress}
							ellipses={getEllipses()}
							downloadMedia={downloadMedia}
							thisMediaIsDownloading={thisMediaIsDownloading}
							isHost={isHost}
							userCanControl={userCanControl}
						/>
					)}
				</div>
			</>
			<Dialog
				ref={dialog}
				xPixel={cancelButtonX}
				yPixel={cancelButtonY}
			/>
		</>
	);
}
